/**
 * Radial Topology Visualizer — Drill-Down Navigator
 * Dark-themed SVG layout showing a single focused node at center
 * with its direct children arranged radially around it.
 *
 * Sprint 34: Drill-down replaces expand/collapse.
 * - Single-click a child to drill into it (becomes new center)
 * - Breadcrumb provides clickable navigation back up
 * - Child count badges show branch depth
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only handles tree rendering
 * - Observer Pattern: Node click/double-click callbacks
 * - Encapsulation: SVG DOM manipulation hidden behind public API
 */

import {
    TopologyNode,
    DEFAULT_LEVEL_COLORS,
    NODE_TYPE_ICONS,
    NODE_LABEL_ICONS,
} from '../topology-types';

// ======================
// Layout Constants
// ======================

const DARK_BG = '#0f172a';
const CARD_FILL = 'rgba(30,41,59,0.9)';
const CARD_WIDTH = 155;
const CARD_HEIGHT = 100;
const CARD_RADIUS = 10;
const CENTER_NODE_RADIUS = 58;
const CHILD_RING_RADIUS = 250;
const EDGE_COLOR = 'rgba(148,163,184,0.3)';
const EDGE_DASH = '4,4';
const LABEL_COLOR = '#e2e8f0';
const SUBLABEL_COLOR = '#94a3b8';

interface NodePosition {
    x: number;
    y: number;
    node: TopologyNode;
}

/**
 * RadialTopologyVisualizer — Drill-down navigator
 * Renders a single focused node at center with its direct children around it.
 */
export class RadialTopologyVisualizer {
    private container: HTMLElement | null = null;
    private svg: SVGSVGElement | null = null;
    private width: number = 960;
    private height: number = 700;
    private nodeClickCallback: ((node: TopologyNode) => void) | null = null;
    private nodeDoubleClickCallback: ((node: TopologyNode) => void) | null = null;
    private positions: Map<string, NodePosition> = new Map();
    private centerX: number = 480;
    private centerY: number = 350;

    /**
     * Initialize the visualizer with a container element
     */
    public initialize(container: HTMLElement): void {
        this.container = container;
        this.width = container.clientWidth || 960;
        this.height = Math.max(600, container.clientHeight || 600);
    }

    /**
     * Set callback for node single-click (drill-down)
     */
    public onNodeClick(callback: (node: TopologyNode) => void): void {
        this.nodeClickCallback = callback;
    }

    /**
     * Set callback for node double-click (detail view)
     */
    public onNodeDoubleClick(callback: (node: TopologyNode) => void): void {
        this.nodeDoubleClickCallback = callback;
    }

    /**
     * Render the topology with focusNode at center and its children around it.
     * Only one level of children is drawn — no recursion.
     */
    public render(focusNode: TopologyNode): void {
        if (!this.container) return;

        // Clear existing
        this.container.innerHTML = '';
        this.positions.clear();

        // Recalculate container dimensions
        this.width = this.container.clientWidth || 960;
        this.height = Math.max(600, this.container.clientHeight || 600);
        this.centerX = this.width / 2;
        this.centerY = (this.height / 2) + 30;

        // Create SVG
        this.svg = this.createSvgElement();

        // Background
        const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        bg.setAttribute('x', '0');
        bg.setAttribute('y', '0');
        bg.setAttribute('width', String(this.width));
        bg.setAttribute('height', String(this.height));
        bg.setAttribute('fill', DARK_BG);
        this.svg.appendChild(bg);

        // Position focus node at center
        this.positions.set(focusNode.id, {
            x: this.centerX,
            y: this.centerY,
            node: focusNode,
        });

        // Position children radially
        const children = focusNode.children;
        if (children.length > 0) {
            const angleStep = (2 * Math.PI) / children.length;
            const startAngle = -Math.PI / 2; // Start from top

            for (let i = 0; i < children.length; i++) {
                const angle = startAngle + i * angleStep;
                const x = this.centerX + CHILD_RING_RADIUS * Math.cos(angle);
                const y = this.centerY + CHILD_RING_RADIUS * Math.sin(angle);
                this.positions.set(children[i].id, {
                    x,
                    y,
                    node: children[i],
                });
            }
        }

        // Draw edges from center to children
        this.drawEdges(focusNode);

        // Draw child nodes first (so center draws on top)
        for (const child of children) {
            const pos = this.positions.get(child.id);
            if (pos) this.drawCardNode(pos);
        }

        // Draw center node
        const centerPos = this.positions.get(focusNode.id);
        if (centerPos) this.drawCenterNode(centerPos);

        // Draw fixed overlays (legend, hint)
        this.drawFixedOverlays();

        this.container.appendChild(this.svg);
    }

    /**
     * Expand a node — kept for API compatibility but no-op in drill-down mode
     */
    public expandNode(_nodeId: string, _rootNode: TopologyNode): void {
        // No-op: drill-down uses render(focusNode) directly
    }

    /**
     * Collapse a node — kept for API compatibility but no-op in drill-down mode
     */
    public collapseNode(_nodeId: string, _rootNode: TopologyNode): void {
        // No-op: drill-down uses render(focusNode) directly
    }

    /**
     * Highlight path — stub for compatibility
     */
    public highlightPath(_nodeId: string): void {
        // No-op
    }

    // ======================
    // SVG Creation
    // ======================

    private createSvgElement(): SVGSVGElement {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', String(this.height));
        svg.setAttribute('viewBox', `0 0 ${this.width} ${this.height}`);
        svg.style.fontFamily = 'ui-sans-serif, system-ui, -apple-system, sans-serif';
        svg.style.overflow = 'hidden';
        svg.style.background = DARK_BG;
        return svg;
    }

    // ======================
    // Fixed Overlays
    // ======================

    private drawFixedOverlays(): void {
        if (!this.svg) return;

        // Hint tooltip at top
        const hint = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        hint.setAttribute('x', String(this.width / 2));
        hint.setAttribute('y', '20');
        hint.setAttribute('text-anchor', 'middle');
        hint.setAttribute('fill', SUBLABEL_COLOR);
        hint.setAttribute('font-size', '11');
        hint.setAttribute('opacity', '0.7');
        hint.textContent = 'Click a node to drill down \u2022 Double-click for details \u2022 Use breadcrumb to navigate back';
        this.svg.appendChild(hint);

        // Legend at bottom-left
        this.drawLegend();
    }

    // ======================
    // Drawing Methods
    // ======================

    private drawEdges(focusNode: TopologyNode): void {
        if (!this.svg) return;

        const parentPos = this.positions.get(focusNode.id);
        if (!parentPos) return;

        for (const child of focusNode.children) {
            const childPos = this.positions.get(child.id);
            if (!childPos) continue;

            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', String(parentPos.x));
            line.setAttribute('y1', String(parentPos.y));
            line.setAttribute('x2', String(childPos.x));
            line.setAttribute('y2', String(childPos.y));
            line.setAttribute('stroke', EDGE_COLOR);
            line.setAttribute('stroke-width', '1.5');
            line.setAttribute('stroke-dasharray', EDGE_DASH);
            this.svg.appendChild(line);
        }
    }

    private drawCenterNode(pos: NodePosition): void {
        if (!this.svg) return;

        const { x, y, node } = pos;
        const color = DEFAULT_LEVEL_COLORS[node.level];

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('class', 'topology-node center-node');
        g.style.cursor = 'pointer';

        // Outer circle
        const outerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        outerCircle.setAttribute('cx', String(x));
        outerCircle.setAttribute('cy', String(y));
        outerCircle.setAttribute('r', String(CENTER_NODE_RADIUS));
        outerCircle.setAttribute('fill', CARD_FILL);
        outerCircle.setAttribute('stroke', color);
        outerCircle.setAttribute('stroke-width', '3');
        g.appendChild(outerCircle);

        // Icon
        const iconPath = this.getIconForNode(node);
        const iconG = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        iconG.setAttribute('transform', `translate(${x - 14}, ${y - 22}) scale(1.3)`);
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', iconPath);
        path.setAttribute('fill', color);
        iconG.appendChild(path);
        g.appendChild(iconG);

        // Label below icon — include child count
        const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        label.setAttribute('x', String(x));
        label.setAttribute('y', String(y + 24));
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('fill', LABEL_COLOR);
        label.setAttribute('font-size', '14');
        label.setAttribute('font-weight', 'bold');
        const labelText = this.truncateLabel(node.label, 16);
        label.textContent = node.children.length > 0
            ? `${labelText} (${node.children.length})`
            : labelText;
        g.appendChild(label);

        this.attachNodeEvents(g, node, outerCircle, color);
        this.svg.appendChild(g);
    }

    private drawCardNode(pos: NodePosition): void {
        if (!this.svg) return;

        const { x, y, node } = pos;
        const color = DEFAULT_LEVEL_COLORS[node.level];
        const halfW = CARD_WIDTH / 2;
        const halfH = CARD_HEIGHT / 2;

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('class', `topology-node level-${node.level}`);
        g.style.cursor = 'pointer';

        // Card background
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', String(x - halfW));
        rect.setAttribute('y', String(y - halfH));
        rect.setAttribute('width', String(CARD_WIDTH));
        rect.setAttribute('height', String(CARD_HEIGHT));
        rect.setAttribute('rx', String(CARD_RADIUS));
        rect.setAttribute('ry', String(CARD_RADIUS));
        rect.setAttribute('fill', CARD_FILL);
        rect.setAttribute('stroke', color);
        rect.setAttribute('stroke-width', '2');
        if (node.metadata?.group_type === 'sub-capability') {
            rect.setAttribute('stroke-dasharray', '4,4');
            rect.setAttribute('fill', 'rgba(30,41,59,0.6)');
        }
        g.appendChild(rect);

        // Icon at top of card
        const iconPath = this.getIconForNode(node);
        const iconG = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        iconG.setAttribute('transform', `translate(${x - 12}, ${y - halfH + 12}) scale(1.0)`);
        const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        pathEl.setAttribute('d', iconPath);
        pathEl.setAttribute('fill', color);
        iconG.appendChild(pathEl);
        g.appendChild(iconG);

        // Label — include child count in label text
        const rawLabel = node.children.length > 0
            ? `${node.label} (${node.children.length})`
            : node.label;
        const lines = this.splitLabel(rawLabel, 16);
        const labelStartY = y + 6;
        const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        label.setAttribute('x', String(x));
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('fill', LABEL_COLOR);
        label.setAttribute('font-size', '13');
        label.setAttribute('font-weight', '600');
        for (let li = 0; li < lines.length; li++) {
            const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
            tspan.setAttribute('x', String(x));
            tspan.setAttribute('y', String(labelStartY + li * 16));
            tspan.textContent = lines[li];
            label.appendChild(tspan);
        }
        g.appendChild(label);

        // Sublabel (level name)
        const levelNames: Record<number, string> = {
            2: 'Business Unit',
            3: 'Capability',
            4: 'Process Flow',
            5: 'Process Flow',
        };
        const sublabelY = labelStartY + lines.length * 16 + 2;
        const sublabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        sublabel.setAttribute('x', String(x));
        sublabel.setAttribute('y', String(sublabelY));
        sublabel.setAttribute('text-anchor', 'middle');
        sublabel.setAttribute('fill', SUBLABEL_COLOR);
        sublabel.setAttribute('font-size', '10');
        sublabel.textContent = levelNames[node.level] || '';
        g.appendChild(sublabel);

        // Child count badge (always shown if node has children)
        if (node.children.length > 0) {
            this.drawChildCountBadge(g, x + halfW - 8, y - halfH + 8, node.children.length, color);
        }

        this.attachNodeEvents(g, node, rect, color);
        this.svg.appendChild(g);
    }

    private drawChildCountBadge(
        g: SVGGElement,
        x: number,
        y: number,
        count: number,
        color: string,
    ): void {
        const badgeBg = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        badgeBg.setAttribute('cx', String(x));
        badgeBg.setAttribute('cy', String(y));
        badgeBg.setAttribute('r', '10');
        badgeBg.setAttribute('fill', color);
        g.appendChild(badgeBg);

        const badgeText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        badgeText.setAttribute('x', String(x));
        badgeText.setAttribute('y', String(y + 4));
        badgeText.setAttribute('text-anchor', 'middle');
        badgeText.setAttribute('fill', '#fff');
        badgeText.setAttribute('font-size', '9');
        badgeText.setAttribute('font-weight', 'bold');
        badgeText.textContent = String(count);
        g.appendChild(badgeText);
    }

    private drawLegend(): void {
        if (!this.svg) return;

        const legendItems: Array<{ label: string; color: string }> = [
            { label: 'Bank', color: DEFAULT_LEVEL_COLORS[1] },
            { label: 'Business Unit', color: DEFAULT_LEVEL_COLORS[2] },
            { label: 'Capability', color: DEFAULT_LEVEL_COLORS[3] },
            { label: 'Process Flow', color: DEFAULT_LEVEL_COLORS[4] },
        ];

        const legendX = 16;
        const legendY = this.height - legendItems.length * 22 - 16;
        const legendBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        legendBg.setAttribute('x', String(legendX));
        legendBg.setAttribute('y', String(legendY - 8));
        legendBg.setAttribute('width', '130');
        legendBg.setAttribute('height', String(legendItems.length * 22 + 16));
        legendBg.setAttribute('rx', '6');
        legendBg.setAttribute('fill', 'rgba(15,23,42,0.85)');
        legendBg.setAttribute('stroke', 'rgba(148,163,184,0.2)');
        legendBg.setAttribute('stroke-width', '1');
        this.svg.appendChild(legendBg);

        legendItems.forEach((item, i) => {
            const iy = legendY + i * 22 + 10;

            const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            dot.setAttribute('cx', String(legendX + 14));
            dot.setAttribute('cy', String(iy));
            dot.setAttribute('r', '5');
            dot.setAttribute('fill', item.color);
            this.svg!.appendChild(dot);

            const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            text.setAttribute('x', String(legendX + 26));
            text.setAttribute('y', String(iy + 4));
            text.setAttribute('fill', SUBLABEL_COLOR);
            text.setAttribute('font-size', '10');
            text.textContent = item.label;
            this.svg!.appendChild(text);
        });
    }

    // ======================
    // Interaction Helpers
    // ======================

    private attachNodeEvents(
        g: SVGGElement,
        node: TopologyNode,
        hoverTarget: SVGElement,
        color: string,
    ): void {
        let clickTimeout: ReturnType<typeof setTimeout> | null = null;

        g.addEventListener('click', (e) => {
            e.stopPropagation();
            if (clickTimeout) clearTimeout(clickTimeout);
            clickTimeout = setTimeout(() => {
                if (this.nodeClickCallback) {
                    this.nodeClickCallback(node);
                }
            }, 220);
        });

        g.addEventListener('dblclick', (e) => {
            e.stopPropagation();
            if (clickTimeout) {
                clearTimeout(clickTimeout);
                clickTimeout = null;
            }
            if (this.nodeDoubleClickCallback) {
                this.nodeDoubleClickCallback(node);
            }
        });

        // Hover effects
        g.addEventListener('mouseenter', () => {
            hoverTarget.setAttribute('stroke-width', '3');
            hoverTarget.setAttribute('stroke', color);
            hoverTarget.style.filter = `drop-shadow(0 0 6px ${color}40)`;
        });
        g.addEventListener('mouseleave', () => {
            const isCenter = hoverTarget.tagName === 'circle' &&
                hoverTarget.getAttribute('r') === String(CENTER_NODE_RADIUS);
            hoverTarget.setAttribute('stroke-width', isCenter ? '3' : '2');
            hoverTarget.style.filter = '';
        });
    }

    // ======================
    // Icon Resolution
    // ======================

    private getIconForNode(node: TopologyNode): string {
        const labelLower = node.label.toLowerCase();
        for (const [keyword, iconPath] of Object.entries(NODE_LABEL_ICONS)) {
            if (labelLower.includes(keyword)) {
                return iconPath;
            }
        }
        return NODE_TYPE_ICONS[node.type] || NODE_TYPE_ICONS['capability'];
    }

    // ======================
    // Utility Methods
    // ======================

    private truncateLabel(label: string, maxChars: number): string {
        return label.length > maxChars
            ? label.substring(0, maxChars - 1) + '\u2026'
            : label;
    }

    /**
     * Split a label into multiple lines that fit within a card.
     */
    private splitLabel(label: string, maxCharsPerLine: number): string[] {
        if (label.length <= maxCharsPerLine) return [label];

        const words = label.split(/\s+/);
        const lines: string[] = [];
        let current = '';

        for (const word of words) {
            if (current.length === 0) {
                current = word;
            } else if ((current + ' ' + word).length <= maxCharsPerLine) {
                current += ' ' + word;
            } else {
                lines.push(current);
                current = word;
            }
        }
        if (current.length > 0) lines.push(current);

        // Cap at 3 lines max
        if (lines.length > 3) {
            lines.length = 3;
            lines[2] = lines[2].substring(0, maxCharsPerLine - 1) + '\u2026';
        }

        return lines;
    }
}
